import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import CellDate from "./components/TableRow/CellDate";
import CellImage from "./components/TableRow/CellImage";
import CellBoolean from "./components/TableRow/CellBoolean";
import CellImagesRender from "./components/TableRow/CellImagesRender";
import {
  ColumnHelper,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  BiChevronLeft,
  BiChevronRight,
  BiChevronsLeft,
  BiChevronsRight,
} from "react-icons/bi";
import { MdModeEdit, MdOpenInNew } from "react-icons/md";
import { IoMdTrash } from "react-icons/io";
import { CSVGoodsFormProps } from "views/admin/goods/hooks/useCSVGoodsForm";
import CellDropdown from "./components/TableRow/CellDropdown";
import { FieldErrors, FieldValues } from "react-hook-form";
import { ICategory } from "views/admin/goods/types/category.interface";
import { IBrand } from "views/admin/goods/types/brand.interface";

interface ILoadOptions {
  options: any[];
  hasMore: boolean;
  additional: {
    page: number;
  };
}
interface ITable<T> {
  hasHeader: boolean;
  tableTitle: string;
  tableData: T[];
  columnHeaders: {
    [key: string]: string | boolean | number | React.ReactNode;
  }[];
  headerActions?: React.ReactNode;
  handleDetail?: (item: T) => void;
  handleUpdate?: (item: T) => void;
  handleDelete?: (item: T) => void;
  handleAddImageRow?: (item: CSVGoodsFormProps) => void;
  loadCategoryOptions?: (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => Promise<ILoadOptions>;
  loadBrandOptions?: (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => Promise<ILoadOptions>;
  onInputCategoryChange?: (value: any, cell: CSVGoodsFormProps) => void;
  onInputBrandChange?: (value: any, cell: CSVGoodsFormProps) => void;
  selectedCategories?: { sku_product: string; value: ICategory }[];
  selectedBrands?: { sku_product: string; value: IBrand }[];
  errors?: FieldErrors<FieldValues>;
}

const columnHelper = <T extends RowData>(): ColumnHelper<T> => {
  return createColumnHelper<T>();
};
const Table = <T extends Record<string, any> = Record<string, any>>(
  props: ITable<T>
) => {
  const {
    hasHeader,
    tableTitle,
    tableData,
    columnHeaders,
    headerActions,
    handleDetail,
    handleUpdate,
    handleDelete,
    handleAddImageRow,
    loadCategoryOptions,
    loadBrandOptions,
    onInputCategoryChange,
    onInputBrandChange,
    selectedCategories,
    selectedBrands,
    errors,
  } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState<T[]>([]);

  const columns = columnHeaders.map((item) => {
    const key = Object.keys(item)[0];
    const value = Object.values(item)[0];

    if (key === "actions") {
      return columnHelper().accessor(key, {
        id: key,
        header: () => (
          <p className="text-sm font-bold text-gray-600 dark:text-white">
            {value}
          </p>
        ),
        cell: (info: any) => (
          <div className="flex items-center gap-5">
            {handleDetail ? (
              <Button
                children={<MdOpenInNew className="text-xl text-white" />}
                onClick={() => handleDetail(info.row.original)}
                size="fit"
                variant="primary"
              />
            ) : (
              <></>
            )}
            <div className="flex items-center gap-2">
              <Button
                children={<MdModeEdit className="text-xl" />}
                onClick={() => handleUpdate(info.row.original)}
                size="fit"
                variant="outlined"
              />
              <Button
                children={<IoMdTrash className="text-xl text-white" />}
                onClick={() => handleDelete(info.row.original)}
                size="fit"
                variant="danger"
              />
            </div>
          </div>
        ),
      });
    }

    return columnHelper().accessor(key, {
      id: key,
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {value}
        </p>
      ),
      cell: (info: any) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    });
  });

  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting,
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  const { setPageSize } = table;

  React.useEffect(() => {
    setData(tableData);
  }, [tableData]);

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      {hasHeader ? (
        <header className="relative flex items-center justify-between px-1 lg:px-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {tableTitle}
          </div>
          {headerActions ? headerActions : <></>}
        </header>
      ) : (
        <></>
      )}

      <div
        className={`overflow-x-scroll 3xl:overflow-x-auto ${
          hasHeader ? "mt-8" : "mt-2"
        }`}
      >
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                <th
                  key={index}
                  className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                >
                  <p className="items-center justify-between text-sm text-gray-600">
                    No.
                  </p>
                </th>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              return (
                <tr key={row.id}>
                  <td key={index} className="min-w-16 border-white/0 py-3 pr-4">
                    <p className="font-semibold text-brand-500">{index + 1}</p>
                  </td>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        className={`border-white/0 py-3 pr-4 ${
                          cell.column.id === "actions"
                            ? "min-w-[unset]"
                            : cell.column.id.includes("_selector")
                            ? "min-w-[200px]"
                            : "min-w-[150px]"
                        }`}
                      >
                        {cell.column.id === "image" ? (
                          <CellImage src={cell.getValue()?.toString()} />
                        ) : typeof cell.getValue() === "boolean" ? (
                          cell.getValue() === true ? (
                            <CellBoolean status={true} text="Ya" />
                          ) : (
                            <CellBoolean status={false} text="Tidak" />
                          )
                        ) : cell.column.id === "category_selector" ? (
                          <CellDropdown
                            loadOptions={loadCategoryOptions}
                            id="category_id"
                            label="Kategori"
                            onInputChange={(value) =>
                              onInputCategoryChange(
                                value,
                                cell.row.original as CSVGoodsFormProps
                              )
                            }
                            placeholder="Pilih Kategori"
                            value={
                              selectedCategories.find(
                                (item: {
                                  sku_product: string;
                                  value: ICategory;
                                }) =>
                                  item.sku_product ===
                                  (cell.row.original as CSVGoodsFormProps)
                                    .sku_product
                              )?.value
                            }
                            errors={errors}
                          />
                        ) : cell.column.id === "brand_selector" ? (
                          <CellDropdown
                            loadOptions={loadBrandOptions}
                            id="brand_id"
                            label="Merk"
                            onInputChange={(value) =>
                              onInputBrandChange(
                                value,
                                cell.row.original as CSVGoodsFormProps
                              )
                            }
                            placeholder="Pilih Merk"
                            value={
                              selectedBrands.find(
                                (item: { sku_product: string; value: any }) =>
                                  item.sku_product ===
                                  (cell.row.original as CSVGoodsFormProps)
                                    .sku_product
                              )?.value
                            }
                            errors={errors}
                          />
                        ) : cell.column.id === "image_link" ? (
                          <CellImagesRender
                            cellData={cell.row.original as CSVGoodsFormProps}
                            onClick={handleAddImageRow}
                          />
                        ) : cell.getValue() ? (
                          ["updated_at", "created_at"].includes(
                            cell.column.id
                          ) ? (
                            <CellDate date={cell.getValue().toString()} />
                          ) : (
                            <div className="line-clamp-3">
                              {typeof cell.getValue() === "number" ? (
                                <p className="text-sm font-semibold">
                                  {cell
                                    .getValue<number>()
                                    .toLocaleString("id-ID")}
                                </p>
                              ) : (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              )}
                            </div>
                          )
                        ) : cell.column.id === "actions" ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        ) : (
                          "-"
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.length > 10 && (
        <div className="pagination mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            Rows per page:
            <select
              value={table.getPageCount()}
              className="flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-2 text-center outline-none dark:!border-white/10 dark:text-white"
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <span>
              Page
              <strong className="mx-1">
                {table.options.state.pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
            <span className="flex items-center gap-1">
              | Go to page:
              <input
                type="number"
                className="flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-2 text-center outline-none dark:!border-white/10 dark:text-white"
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                style={{ width: "50px" }}
              />
            </span>
          </div>
          <div className="flex gap-2">
            <button
              className="rounded-lg bg-white p-2 text-brand-500 text-opacity-90"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <BiChevronsLeft className="text-xl" />
            </button>
            <button
              className="rounded-lg bg-brand-500 p-2 text-white text-opacity-90"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <BiChevronLeft className="text-xl" />
            </button>
            <button
              className="rounded-lg bg-brand-500 p-2 text-white text-opacity-90"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <BiChevronRight className="text-xl" />
            </button>
            <button
              className="rounded-lg bg-white p-2 text-brand-500 text-opacity-90"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <BiChevronsRight className="text-xl" />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default Table;
